
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useTranslations } from "@/hooks/useTranslations";

interface ProfileAvatarProps {
  studentId: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string;
  onAvatarUpdate: (url: string) => void;
}

export const ProfileAvatar = ({
  studentId,
  firstName,
  lastName,
  avatarUrl,
  onAvatarUpdate,
}: ProfileAvatarProps) => {
  const { t } = useTranslations("settings");
  
  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) {
        return;
      }
      
      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const filePath = `${studentId}/avatar.${fileExt}`;

      console.log("Uploading avatar:", filePath);
      
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('students')
        .update({ avatar_url: publicUrl })
        .eq('id', studentId);

      if (updateError) throw updateError;

      onAvatarUpdate(publicUrl);
      toast.success("Foto de perfil atualizada com sucesso!");
    } catch (error: any) {
      console.error("Error uploading avatar:", error);
      toast.error("Erro ao atualizar foto de perfil");
    }
  };
  
  const handleRemoveAvatar = async () => {
    try {
      if (!avatarUrl) return;
      
      const { error: updateError } = await supabase
        .from('students')
        .update({ avatar_url: null })
        .eq('id', studentId);

      if (updateError) throw updateError;

      onAvatarUpdate("");
      toast.success("Foto de perfil removida com sucesso!");
    } catch (error: any) {
      console.error("Error removing avatar:", error);
      toast.error("Erro ao remover foto de perfil");
    }
  };

  return (
    <div className="flex flex-col items-center mb-6">
      <Avatar className="h-24 w-24 mb-4">
        <AvatarImage src={avatarUrl} />
        <AvatarFallback>
          {firstName?.[0]}{lastName?.[0]}
        </AvatarFallback>
      </Avatar>
      <div className="flex flex-wrap gap-2 justify-center w-full">
        <Input
          type="file"
          accept="image/*"
          onChange={handleAvatarUpload}
          className="hidden"
          id="avatar-upload"
        />
        <Button
          variant="outline"
          onClick={() => document.getElementById('avatar-upload')?.click()}
          className="flex-1 min-w-[140px] max-w-[200px]"
        >
          <Upload className="mr-2 h-4 w-4" />
          {t("profile.uploadPhoto")}
        </Button>
        {avatarUrl && (
          <Button
            variant="destructive"
            onClick={handleRemoveAvatar}
            className="flex-1 min-w-[140px] max-w-[200px]"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t("profile.removePhoto")}
          </Button>
        )}
      </div>
    </div>
  );
};
